import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider, useI18n } from "@/contexts/I18nContext";
import { EngagementProvider } from "@/contexts/EngagementContext";
import Layout from "@/components/Layout";
import NewVersionBanner from "@/components/NewVersionBanner";   // Batch 27 · Add-on 3 · C (M41)
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import FirmDashboard from "@/pages/FirmDashboard";   // Stage 2 · 2.3 ONE DASHBOARD (replaces pages/Dashboard.jsx + pages/Executive.jsx)
import { LEGACY_DASHBOARD_PATHS } from "@/components/dashboard/fdTheme";
import Engagements from "@/pages/Engagements";
import GLImport from "@/pages/GLImport";
import Analytics from "@/pages/Analytics";
import Sampling from "@/pages/Sampling";
import Vouching from "@/pages/Vouching";
import Documents from "@/pages/Documents";
import DocSummaries from "@/pages/DocSummaries";
import Discrepancies from "@/pages/Discrepancies";
import Settings from "@/pages/Settings";
import IFRS from "@/pages/IFRS";
import FsMapping from "@/pages/FsMapping";
import Assistant from "@/pages/Assistant";
import AcceptInvite from "@/pages/AcceptInvite";
import AuditTrail from "@/pages/AuditTrail";
import RiskAssessment from "@/pages/RiskAssessment";
import RecycleBin from "@/pages/RecycleBin";
import Ratios from "@/pages/Ratios";
import Aging from "@/pages/Aging";
import DigitalAudit from "@/pages/DigitalAudit";
const Platform = React.lazy(() => import("@/pages/Platform"));
const Methodology = React.lazy(() => import("@/pages/Methodology"));
const DisclosureChecklist = React.lazy(() => import("@/pages/DisclosureChecklist"));
import ResetPassword from "@/pages/ResetPassword";
import VerifyLogin from "@/pages/VerifyLogin";
import Install from "@/pages/Install";   // Batch 19 · Stage C (C2): public install page
import BackupCodesSetup from "@/pages/BackupCodesSetup";
import RegenBackupCodes from "@/pages/RegenBackupCodes";
import StepUpDialog from "@/components/StepUpDialog";
import AiExhaustedGateModal from "@/components/AiExhaustedGateModal";
import AppDialogHost from "@/components/AppDialog";   // Batch 15 · N4 (F-118)
import Planning from "@/pages/Planning";
import FormsPage from "@/pages/Forms";
import RiskAssessmentForms from "@/pages/RiskAssessmentForms";
import Completion from "@/pages/Completion";
import FinalAnalyticalReviewPage from "@/pages/FinalAnalyticalReviewPage";   // Batch 33 · B33-1
import Specialized from "@/pages/Specialized";
import DocumentLibrary from "@/pages/DocumentLibrary";
import HelpCenter from "@/pages/HelpCenter";
import Billing from "@/pages/Billing";
import Confirmations from "@/pages/Confirmations";
import BundleVerifier from "@/pages/BundleVerifier";
import GLEntryView from "@/pages/GLEntryView";
import ISQM, { ISQMFirmForm, ISQMEngForm } from "@/pages/ISQM";
const AIUsage = React.lazy(() => import("@/pages/AIUsage"));
import ReviewPoints from "@/pages/ReviewPoints";
import PageTitle from "@/components/PageTitle";
import HeadFolders from "@/pages/HeadFolders";
import AuditOnlyGuard from "@/components/AuditOnlyGuard";
import W171Acceptance from "@/pages/review/W17_1_Acceptance";
import W172Understanding from "@/pages/review/W17_2_Understanding";
import W173Analytical from "@/pages/review/W17_3_Analytical";
import W174Inquiries from "@/pages/review/W17_4_Inquiries";
import W175Specific from "@/pages/review/W17_5_Specific";
import W176Reconciliation from "@/pages/review/W17_6_Reconciliation";
import W177Register from "@/pages/review/W17_7_Register";
import W178Conclusion from "@/pages/review/W17_8_Conclusion";

// Batch 0 Phase 1 — Placeholder page shells. Each wraps <PlaceholderPage />
// with its own title/purpose keys so Phase 2+ can drop in the real forms
// area by just replacing the component export here (route stays stable).
const AuditResponsePage = React.lazy(() => import("@/pages/AuditResponsePlan"));
const ScopingPage = React.lazy(() => import("@/pages/Scoping"));
const ControlsWalkthroughsPage = React.lazy(() => import("@/pages/Walkthroughs"));
const ControlsITGCPage = React.lazy(() => import("@/pages/ITGC"));
const ControlsA9002Page = React.lazy(() => import("@/pages/TestsOfControls"));
const ControlsDeficiencyLogPage = React.lazy(() => import("@/pages/DeficiencyLog"));
const HeadFoldersPage = HeadFolders;
const FieldworkA9001Page = React.lazy(() => import("@/pages/JournalEntryTesting"));
// Batch C · Round C1 · W7 — Opening Balances page (screen name hides A9003 per R8).
const FieldworkA9003Page = React.lazy(() => import("@/pages/OpeningBalances"));
const AdjustingEntriesPage = React.lazy(() => import("@/pages/AdjustingEntries"));   // Batch 13 · AE5
const NotFoundPage = React.lazy(() => import("@/pages/NotFound"));                      // Batch 13 · B6
const MisstatementsPage = React.lazy(() => import("@/pages/Misstatements"));
const AuditSummaryMemoPage = React.lazy(() => import("@/pages/AuditSummaryMemo"));
const FileAssemblyPage = React.lazy(() => import("@/pages/FileAssembly"));
const EqReviewLinkPage = React.lazy(() => import("@/pages/EqReview")); // Round 27 · X1 — real EQ review record (522(a))
const ClearanceGatePage = React.lazy(() => import("@/pages/ClearanceGate"));
const AuditorsReportPage = React.lazy(() => import("@/pages/AuditorReport"));
const KamRegisterPage = React.lazy(() => import("@/pages/KamRegister"));
const OtherInformationPage = React.lazy(() => import("@/pages/OtherInformation"));
const FirmReportFormatsPage = React.lazy(() => import("@/pages/FirmReportFormats"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="p-8 text-sm text-muted-foreground">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <PageTitle />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login/verify" element={<VerifyLogin />} />
        <Route path="/install" element={<Install />} />
        <Route path="/backup-codes-setup" element={<Protected><BackupCodesSetup /></Protected>} />
        <Route path="/platform/tools/regen-backup-codes" element={<Protected><RegenBackupCodes /></Protected>} />
        <Route
          path="/*"
          element={
            <Protected>
              <EngagementProvider>
                <ErrorBoundary>
                  <Layout />
                </ErrorBoundary>
              </EngagementProvider>
            </Protected>
          }
        >
          <Route index element={<FirmDashboard />} />
          {/* Stage 2 · 2.3: the old Executive / workspace dashboard URLs land on the one Dashboard */}
          {LEGACY_DASHBOARD_PATHS.map((p) => <Route key={p} path={p} element={<Navigate to="/" replace />} />)}
          <Route path="engagements" element={<Engagements />} />
          <Route path="gl-import" element={<GLImport />} />
          <Route path="planning" element={<Planning />} />
          <Route path="risk-assessment-forms" element={<RiskAssessmentForms />} />
          <Route path="risk" element={<RiskAssessment />} />
          <Route path="recycle-bin" element={<RecycleBin />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="ratios" element={<Ratios />} />
          <Route path="aging" element={<Navigate to="/head-folders?open=1203-06" replace />} />
          <Route path="aging/legacy" element={<Aging />} />
          <Route path="cutoff" element={<Navigate to="/head-folders?open=4101-06" replace />} />
          <Route path="cut-off" element={<Navigate to="/head-folders?open=4101-06" replace />} />
          <Route path="digital-audit" element={<DigitalAudit />} />
          <Route path="digital-audit/correlation" element={<DigitalAudit />} />
          <Route path="digital-audit/sub-ledger" element={<DigitalAudit />} />
          <Route path="digital-audit/trade-payable" element={<DigitalAudit />} />
          <Route path="digital-audit/inventory" element={<DigitalAudit />} />
          <Route path="sampling" element={<AuditOnlyGuard pageId="/sampling"><Sampling /></AuditOnlyGuard>} />
          <Route path="vouching" element={<AuditOnlyGuard pageId="/vouching"><Vouching /></AuditOnlyGuard>} />
          <Route path="documents" element={<Documents />} />
          <Route path="doc-summaries" element={<DocSummaries />} />
          <Route path="confirmations" element={<AuditOnlyGuard pageId="/confirmations"><Confirmations /></AuditOnlyGuard>} />
          <Route path="discrepancies" element={<Discrepancies />} />
          <Route path="ifrs" element={<IFRS />} />
          <Route path="ifrs/:eng_id/mapping" element={<FsMapping />} />
          <Route path="completion" element={<Completion />} />
          <Route path="final-analytical-review" element={<FinalAnalyticalReviewPage />} />
          <Route path="specialized" element={<Specialized />} />
          <Route path="document-library" element={<DocumentLibrary />} />
          <Route path="assistant" element={<Assistant />} />
          <Route path="audit-trail" element={<AuditTrail />} />
          <Route path="platform" element={<React.Suspense fallback={null}><Platform /></React.Suspense>} />
          <Route path="methodology" element={<React.Suspense fallback={null}><Methodology /></React.Suspense>} />
          <Route path="disclosure-checklist" element={<React.Suspense fallback={null}><DisclosureChecklist /></React.Suspense>} />
          <Route path="verify-bundle" element={<BundleVerifier />} />
          <Route path="gl-entry/:id" element={<GLEntryView />} />
          <Route path="settings" element={<Settings />} />
          <Route path="billing" element={<Billing />} />
          <Route path="isqm" element={<ISQM />} />
          <Route path="isqm/firm/:formId" element={<ISQMFirmForm />} />
          <Route path="isqm/engagement/:engId/form/:formId" element={<ISQMEngForm />} />
          <Route path="ai-usage" element={<React.Suspense fallback={null}><AIUsage /></React.Suspense>} />
          <Route path="review-points" element={<ReviewPoints />} />
          {/* Batch 0 Phase 1 — new sidebar routes (placeholders until Phase 2+) */}
          <Route path="acceptance" element={<FormsPage tabId="acceptance" />} />
          <Route path="review/w17-1-acceptance" element={<W171Acceptance />} />
          <Route path="review/w17-2-understanding" element={<W172Understanding />} />
          <Route path="review/w17-3-analytical" element={<W173Analytical />} />
          <Route path="review/w17-4-inquiries" element={<W174Inquiries />} />
          <Route path="review/w17-5-specific" element={<W175Specific />} />
          <Route path="review/w17-6-reconciliation" element={<W176Reconciliation />} />
          <Route path="review/w17-7-register" element={<W177Register />} />
          <Route path="review/w17-8-conclusion" element={<W178Conclusion />} />
          <Route path="audit-response" element={<AuditOnlyGuard pageId="/audit-response"><AuditResponsePage /></AuditOnlyGuard>} />
          <Route path="scoping" element={<ScopingPage />} />
          <Route path="controls/walkthroughs" element={<AuditOnlyGuard pageId="/controls/walkthroughs"><ControlsWalkthroughsPage /></AuditOnlyGuard>} />
          <Route path="controls/itgc" element={<AuditOnlyGuard pageId="/controls/itgc"><ControlsITGCPage /></AuditOnlyGuard>} />
          <Route path="controls/a9002" element={<AuditOnlyGuard pageId="/controls/a9002"><ControlsA9002Page /></AuditOnlyGuard>} />
          <Route path="controls/203a" element={<AuditOnlyGuard pageId="/controls/203a"><FormsPage tabId="controls" titleKey="layoutNav.items.controls_203a" /></AuditOnlyGuard>} />
          <Route path="controls/deficiency-log" element={<AuditOnlyGuard pageId="/controls/deficiency-log"><ControlsDeficiencyLogPage /></AuditOnlyGuard>} />
          <Route path="head-folders" element={<HeadFoldersPage />} />
          <Route path="fieldwork/a9001" element={<AuditOnlyGuard pageId="/fieldwork/a9001"><FieldworkA9001Page /></AuditOnlyGuard>} />
          <Route path="fieldwork/a9003" element={<AuditOnlyGuard pageId="/fieldwork/a9003"><FieldworkA9003Page /></AuditOnlyGuard>} />
          <Route path="fieldwork/adjusting-entries" element={<AuditOnlyGuard pageId="/fieldwork/adjusting-entries"><React.Suspense fallback={null}><AdjustingEntriesPage /></React.Suspense></AuditOnlyGuard>} />
          <Route path="confirmations-forms" element={<FormsPage tabId="confirmations_forms" />} />
          <Route path="completion/misstatements" element={<MisstatementsPage />} />
          <Route path="completion/summary-memo" element={<AuditSummaryMemoPage />} />
          <Route path="completion/eq-review" element={<EqReviewLinkPage />} />
          <Route path="completion/clearance-gate" element={<ClearanceGatePage />} />
          <Route path="reporting/auditors-report" element={<AuditOnlyGuard pageId="/reporting/auditors-report"><React.Suspense fallback={null}><AuditorsReportPage /></React.Suspense></AuditOnlyGuard>} />
          <Route path="reporting/firm-report-formats" element={<React.Suspense fallback={null}><FirmReportFormatsPage /></React.Suspense>} />
          <Route path="completion/kam-register" element={<React.Suspense fallback={null}><KamRegisterPage /></React.Suspense>} />
          <Route path="completion/other-information" element={<React.Suspense fallback={null}><OtherInformationPage /></React.Suspense>} />
          <Route path="close/file-assembly" element={<FileAssemblyPage />} />
          <Route path="help" element={<HelpCenter />} />
          <Route path="help/:slug" element={<HelpCenter />} />
          <Route path="*" element={<React.Suspense fallback={null}><NotFoundPage /></React.Suspense>} />   {/* Batch 13 · B6 (F-98) */}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <AppRoutes />
          <StepUpDialog />
          <AiExhaustedGateModal />
          <AppDialogHost />
          <Toaster position="top-center" richColors closeButton />
          <NewVersionBanner />
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}
